import { z } from 'zod';

/**
 * Review-queue kinds written by the dt_spar import (apps/api/src/import/dtspar). Each item is
 * something a person has to decide in People; the import never guesses. M6 adds the live
 * hygiene queues (locked accounts, unmatched initials seen on invoices, …) alongside these.
 */
export const ReviewQueueKindSchema = z.enum([
  'position_conflict',
  'position_unresolved',
  'site_unmatched',
  'role_unmapped',
  'duplicate_initials',
  'missing_staff_number',
  'login_email_missing',
  'login_email_conflict',
  'active_login_on_left_staff',
  'line_manager_unresolved',
  'mailbox_login_not_holder',
  'duplicate_logins',
  'external_login',
  /** Phase 2a M1: holiday import (apps/api/src/import/holidays). */
  'holiday_disagreement',
  'holiday_allocation_missing',
  'holiday_person_unknown',
  'working_pattern_missing',
  /** Phase 2a M4: an invariant the weekly holiday-integrity job found broken. */
  'holiday_integrity',
  /** Phase 2b M1: absence import (apps/api/src/import/absence). */
  'absence_person_unknown',
  'absence_reason_unmapped',
  'absence_statutory_unclear',
  'absence_overlap',
  /** Phase 3 M1: clockings whose old staff id matches nobody in Aroo (attendance sync). */
  'clock_staff_unknown',
]);

/**
 * Queues computed from live data rather than stored review items. The `unmatched_*` ones read
 * dt_spar (read-only, cached five minutes); the rest come from Aroo's own tables.
 */
export const ComputedQueueKindSchema = z.enum([
  'vacant_positions',
  'unmatched_initials',
  'unmatched_extensions',
  'unmatched_quartix_drivers',
  'locked_accounts',
  'expiring_access',
  'bridge_failed',
  /** Phase 2a M2b: trade staff with Saturday in a one-week pattern nobody has confirmed (D39). */
  'saturday_alternation_unset',
  /** Phase 2a M2b: imported pending requests whose dates have passed. */
  'stale_pending_requests',
  /** Phase 2b M2: closed sickness/AWOL with no return-to-work after 3 working days. */
  'rtw_overdue',
  /** S-M2 (§6.5): accounts whose Autopart Rep initials match a person at the home branch. */
  'suggested_account_managers',
]);

export const QueueKindSchema = z.union([ReviewQueueKindSchema, ComputedQueueKindSchema]);

/** Review kinds owned by the holiday import; the people import leaves them alone. */
export const HOLIDAY_QUEUE_KINDS = [
  'holiday_disagreement',
  'holiday_allocation_missing',
  'holiday_person_unknown',
  'working_pattern_missing',
] as const satisfies readonly z.infer<typeof ReviewQueueKindSchema>[];

/** Review kinds owned by the attendance clock sync (Phase 3 M1). */
export const ATTENDANCE_QUEUE_KINDS = ['clock_staff_unknown'] as const satisfies readonly z.infer<
  typeof ReviewQueueKindSchema
>[];

/** Review kinds owned by the absence import (Phase 2b M1). */
export const ABSENCE_QUEUE_KINDS = [
  'absence_person_unknown',
  'absence_reason_unmapped',
  'absence_statutory_unclear',
  'absence_overlap',
] as const satisfies readonly z.infer<typeof ReviewQueueKindSchema>[];

export const QUEUE_LABELS: Record<z.infer<typeof QueueKindSchema>, string> = {
  position_conflict: 'Seats with more than one candidate',
  position_unresolved: 'Managers with no matching seat',
  site_unmatched: 'People at an unknown site',
  role_unmapped: 'People with no role',
  duplicate_initials: 'Duplicate Autopart initials',
  missing_staff_number: 'Missing staff numbers',
  login_email_missing: 'Logins with no email address',
  login_email_conflict: 'Logins sharing an email address',
  active_login_on_left_staff: 'Active logins on left staff',
  line_manager_unresolved: 'Line managers to confirm',
  mailbox_login_not_holder: 'Mailbox logins linked to a non-holder',
  duplicate_logins: 'People with more than one login',
  external_login: 'Logins with no staff record',
  holiday_disagreement: 'Holiday balances that disagree with the old system',
  holiday_allocation_missing: 'People with no holiday allocation',
  holiday_person_unknown: 'Old holiday records for unknown people',
  working_pattern_missing: 'People with no working pattern',
  holiday_integrity: 'Holiday data checks that failed',
  absence_person_unknown: 'Old absence records for unknown people',
  absence_reason_unmapped: 'Old absence reasons with no Aroo type',
  absence_statutory_unclear: 'Statutory leave whose dates are unclear',
  absence_overlap: 'Imported absences that overlap another',
  clock_staff_unknown: 'Clockings for unknown staff',
  vacant_positions: 'Vacant positions',
  unmatched_initials: 'Unmatched Autopart initials (last 30 days)',
  unmatched_extensions: 'Unmatched phone extensions',
  unmatched_quartix_drivers: 'Unmatched Quartix drivers',
  locked_accounts: 'Locked accounts',
  expiring_access: 'Access expiring this week',
  bridge_failed: 'Old-system writes that failed',
  saturday_alternation_unset: 'Saturday alternation not set',
  stale_pending_requests: 'Imported requests still pending after their dates',
  rtw_overdue: 'Return-to-work conversations overdue',
  suggested_account_managers: 'Suggested account managers (from Autopart Rep)',
};

export const QueueCountSchema = z.object({
  kind: QueueKindSchema,
  label: z.string(),
  count: z.number().int().nonnegative(),
  /** Set when the source (dt_spar) could not be read; the count is then 0 and stale. */
  unavailable: z.boolean().optional(),
});

/** A person the queue proposes for an unmatched identity, with why. */
export const QueueSuggestionSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  reason: z.string(),
  /** 0–1 where the source gives one (extension map), else by rule. */
  confidence: z.number().min(0).max(1),
});
export type QueueSuggestion = z.infer<typeof QueueSuggestionSchema>;

export const QueueCountsResponseSchema = z.object({
  queues: z.array(QueueCountSchema),
  /** When the last import that wrote these items ran; null if never. */
  lastImportAt: z.iso.datetime().nullable(),
});

export const QueueItemSchema = z.object({
  id: z.string(),
  /** Legacy id, position code or site code: never a name. */
  legacyRef: z.string(),
  /** Rule-specific facts; may include display names resolved from Aroo data. */
  details: z.record(z.string(), z.unknown()),
  /** Aroo person this item is about, when it maps to one. */
  personId: z.uuid().nullable(),
  personName: z.string().nullable(),
  createdAt: z.iso.datetime(),
});

export const QueueListResponseSchema = z.object({
  kind: QueueKindSchema,
  label: z.string(),
  items: z.array(QueueItemSchema),
  /** Why the list may be incomplete (dt_spar unreachable). */
  unavailable: z.string().nullable().optional(),
});

export type ReviewQueueKind = z.infer<typeof ReviewQueueKindSchema>;
export type ComputedQueueKind = z.infer<typeof ComputedQueueKindSchema>;
export type QueueKind = z.infer<typeof QueueKindSchema>;
export type QueueCount = z.infer<typeof QueueCountSchema>;
export type QueueCountsResponse = z.infer<typeof QueueCountsResponseSchema>;
export type QueueItem = z.infer<typeof QueueItemSchema>;
export type QueueListResponse = z.infer<typeof QueueListResponseSchema>;
